import * as THREE from 'three';

// ── Palettes (one per section) ────────────────────────────────────────────────
// Colors are intentionally dark - a CSS veil sits above the canvas so these
// read as atmosphere rather than foreground. Match the portfolio's violet theme.
export const SECTION_PALETTES: Record<string, { bg: string; c0: string; c1: string }> = {
  hero:         { bg: '#0a0a0b', c0: '#2a1650', c1: '#131019' }, // near-black, faint violet
  works:        { bg: '#0a0a0b', c0: '#16101f', c1: '#0c0b10' }, // almost invisible - cards lead
  about:        { bg: '#0f0c14', c0: '#4c1d95', c1: '#1e1033' }, // violet
  experience:   { bg: '#0a0f18', c0: '#1e3a6e', c1: '#090d18' }, // deep blue
  certificates: { bg: '#130a18', c0: '#6b21a8', c1: '#1a0a2e' }, // purple
};

const DEFAULT_PALETTE = SECTION_PALETTES['hero']!;

// ── Inline GLSL ───────────────────────────────────────────────────────────────
const SPHERE_VERT = /* glsl */`
attribute vec3 a_instanceNoiseOffset;
attribute float a_id;

uniform float u_noiseOffset;
uniform vec3 u_lightPosition;
uniform vec4 u_instancePlacements[SPHERE_COUNT];

varying vec3 v_worldNormal;
varying vec3 v_worldPosition;
varying vec3 v_viewPosition;
varying vec3 v_noisePosition;
varying float v_ao;
varying float v_shadow;

float sphOcclusion(in vec3 pos, in vec3 nor, in vec4 sph) {
  vec3  di = sph.xyz - pos;
  float l  = length(di);
  float nl = dot(nor, di / l);
  float h  = l / sph.w;
  float h2 = h * h;
  float k2 = 1.0 - h2 * nl * nl;
  float res = max(0.0, nl) / h2;
  if (k2 > 0.0) res = pow(clamp(0.5 * (nl * h + 1.0) / h2, 0.0, 1.0), 1.5);
  return res;
}

float sphSoftShadow(in vec3 ro, in vec3 rd, in vec4 sph, in float k) {
  sph.w *= 0.8;
  vec3  oc = ro - sph.xyz;
  float b  = dot(oc, rd);
  float c  = dot(oc, oc) - sph.w * sph.w;
  float h  = b * b - c;
  return (b > 0.0) ? step(-0.0001, c) : smoothstep(0.0, 1.0, h * k / b);
}

void main() {
  vec4 mvPosition  = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
  v_worldPosition  = (modelMatrix * instanceMatrix * vec4(position, 1.0)).xyz;
  v_noisePosition  = position + vec3(0.0, 0.0, u_noiseOffset) + a_instanceNoiseOffset;
  v_viewPosition   = -mvPosition.xyz;
  v_worldNormal    = mat3(instanceMatrix) * normal;
  vec3 lightDir    = normalize(u_lightPosition - v_worldPosition);
  v_ao = 1.0; v_shadow = 1.0;
  for (int i = 0; i < SPHERE_COUNT; i++) {
    if (abs(float(i) - a_id) > 0.5) {
      v_ao     = min(v_ao,     1.0 - sphOcclusion(v_worldPosition, v_worldNormal, u_instancePlacements[i]));
      v_shadow = min(v_shadow, sphSoftShadow(v_worldPosition, lightDir, u_instancePlacements[i], 2.0));
    }
  }
  v_ao = pow(v_ao, 2.0);
  gl_Position = projectionMatrix * mvPosition;
}
`;

const SPHERE_FRAG = /* glsl */`
uniform vec3 u_color0;
uniform vec3 u_color1;
uniform vec3 u_colorBg;
uniform vec3 u_lightPosition;
uniform float u_opacity;

varying vec3 v_worldNormal;
varying vec3 v_worldPosition;
varying vec3 v_viewPosition;
varying vec3 v_noisePosition;
varying float v_ao;
varying float v_shadow;

vec3 nhash(vec3 p) {
  p = vec3(dot(p,vec3(127.1,311.7,74.7)), dot(p,vec3(269.5,183.3,246.1)), dot(p,vec3(113.5,271.9,124.6)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}
float noise(in vec3 p) {
  vec3 i = floor(p); vec3 f = fract(p); vec3 u = f*f*(3.0-2.0*f);
  return mix(mix(mix(dot(nhash(i),f),dot(nhash(i+vec3(1,0,0)),f-vec3(1,0,0)),u.x),
                 mix(dot(nhash(i+vec3(0,1,0)),f-vec3(0,1,0)),dot(nhash(i+vec3(1,1,0)),f-vec3(1,1,0)),u.x),u.y),
             mix(mix(dot(nhash(i+vec3(0,0,1)),f-vec3(0,0,1)),dot(nhash(i+vec3(1,0,1)),f-vec3(1,0,1)),u.x),
                 mix(dot(nhash(i+vec3(0,1,1)),f-vec3(0,1,1)),dot(nhash(i+vec3(1,1,1)),f-vec3(1,1,1)),u.x),u.y),u.z);
}
float aastep(float a, float b) { float d=b-a; return smoothstep(a-fwidth(d),a,b); }

float D_GGX(float NoH,float a){float a2=a*a;float f=(NoH*a2-NoH)*NoH+1.0;return a2/(3.1415926*f*f);}
float V_SmithGGX(float NoV,float NoL,float r){float a=r;float V=NoL*(NoV*(1.0-a)+a);float L=NoV*(NoL*(1.0-a)+a);return 0.5/(V+L+0.002);}
vec3 F_Schlick(float VoH,vec3 f0){float f=pow(1.0-VoH,5.0);return f+f0*(1.0-f);}
vec3 getSpec(vec3 v,vec3 n,vec3 l,float r,vec3 sc){
  vec3 h=normalize(v+l);
  float NoV=abs(dot(n,v))+1e-5,NoL=clamp(dot(n,l),0.,1.),NoH=clamp(dot(n,h),0.,1.),LoH=clamp(dot(l,h),0.,1.);
  return (D_GGX(NoH,r*r)*V_SmithGGX(NoV,NoL,r))*F_Schlick(LoH,sc);
}
float hash12(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}

void main() {
  float fogRatio = smoothstep(4.5, 6.0, v_viewPosition.z);
  float n = noise(v_noisePosition);
  n = abs(noise(n*3.0+100.0+v_noisePosition));
  float cr = aastep(0.2, n);
  vec3 color = mix(u_color0, u_color1, cr);
  vec3 lp = u_lightPosition;
  vec3 ld = normalize(lp - v_worldPosition);
  vec3 wn = normalize(v_worldNormal);
  vec3 vd = normalize(cameraPosition - v_worldPosition);
  float r = (1.0-cr)*0.5+0.5;
  vec3 sc = color*0.7+vec3(0.3);
  float br = min(1.0,1.0/length(lp-v_worldPosition)); br*=br;
  float diff = max(0.0,dot(wn,ld))/3.1415926*br;
  vec3 spec = getSpec(vd,wn,ld,r,sc)*br*0.15;
  color = (color+0.03)*(0.85+diff)+spec;
  color = mix(color*(v_ao*0.65+0.35*v_shadow), u_colorBg, max(1.0-u_opacity,fogRatio));
  gl_FragColor = vec4(color + hash12(gl_FragCoord.xy)/255.0, 1.0);
}
`;

const BACKDROP_VERT = /* glsl */`
uniform float u_noiseOffset;
uniform vec3 u_noiseOffsetVector;
varying vec2 v_uv;
varying vec3 v_worldNormal;
varying vec3 v_worldPosition;
varying vec3 v_noisePosition;
vec2 rot2(vec2 v,float a){float s=sin(a),c=cos(a);return mat2(c,-s,s,c)*v;}
void main(){
  v_uv=uv; v_worldNormal=normal;
  vec4 mp=modelMatrix*vec4(position,1.0); v_worldPosition=mp.xyz;
  v_noisePosition=vec3(rot2(v_uv,-0.52359),0.0)*vec3(2.5,1.0,1.0)*10.0
    +vec3(0.0,u_noiseOffset*2.0,u_noiseOffset)+u_noiseOffsetVector;
  gl_Position=projectionMatrix*viewMatrix*mp;
}
`;

const BACKDROP_FRAG = /* glsl */`
uniform vec3 u_color0; uniform vec3 u_color1; uniform vec3 u_colorBg;
varying vec2 v_uv; varying vec3 v_worldNormal; varying vec3 v_worldPosition; varying vec3 v_noisePosition;

vec3 nhash(vec3 p){p=vec3(dot(p,vec3(127.1,311.7,74.7)),dot(p,vec3(269.5,183.3,246.1)),dot(p,vec3(113.5,271.9,124.6)));return -1.0+2.0*fract(sin(p)*43758.5453123);}
float noise(in vec3 p){vec3 i=floor(p);vec3 f=fract(p);vec3 u=f*f*(3.0-2.0*f);return mix(mix(mix(dot(nhash(i),f),dot(nhash(i+vec3(1,0,0)),f-vec3(1,0,0)),u.x),mix(dot(nhash(i+vec3(0,1,0)),f-vec3(0,1,0)),dot(nhash(i+vec3(1,1,0)),f-vec3(1,1,0)),u.x),u.y),mix(mix(dot(nhash(i+vec3(0,0,1)),f-vec3(0,0,1)),dot(nhash(i+vec3(1,0,1)),f-vec3(1,0,1)),u.x),mix(dot(nhash(i+vec3(0,1,1)),f-vec3(0,1,1)),dot(nhash(i+vec3(1,1,1)),f-vec3(1,1,1)),u.x),u.y),u.z);}
float aastep(float a,float b){float d=b-a;return smoothstep(a-fwidth(d),a,b);}
const float eps=0.0001;
vec3 rgb2hsl(vec3 c){float mn=min(c.r,min(c.g,c.b)),mx=max(c.r,max(c.g,c.b));vec3 mask=step(c.grr,c.rgb)*step(c.bbg,c.rgb);vec3 h=mask*(vec3(0.0,2.0,4.0)+(c.gbr-c.brg)/(mx-mn+eps))/6.0;return vec3(fract(1.0+h.x+h.y+h.z),(mx-mn)/(1.0-abs(mn+mx-1.0)+eps),(mn+mx)*0.5);}
vec3 hsl2rgb(vec3 c){vec3 rgb=clamp(abs(mod(c.x*6.0+vec3(0,4,2),6.0)-3.0)-1.0,0.0,1.0);return c.z+c.y*(rgb-0.5)*(1.0-abs(2.0*c.z-1.0));}
float D_GGX(float NoH,float a){float a2=a*a;float f=(NoH*a2-NoH)*NoH+1.0;return a2/(3.1415926*f*f);}
float V_SmithGGX(float NoV,float NoL,float r){float a=r;float V=NoL*(NoV*(1.0-a)+a);float L=NoV*(NoL*(1.0-a)+a);return 0.5/(V+L+0.002);}
vec3 F_Schlick(float VoH,vec3 f0){float f=pow(1.0-VoH,5.0);return f+f0*(1.0-f);}
vec3 getSpec(vec3 v,vec3 n,vec3 l,float r,vec3 sc){vec3 h=normalize(v+l);float NoV=abs(dot(n,v))+1e-5,NoL=clamp(dot(n,l),0.,1.),NoH=clamp(dot(n,h),0.,1.),LoH=clamp(dot(l,h),0.,1.);return(D_GGX(NoH,r*r)*V_SmithGGX(NoV,NoL,r))*F_Schlick(LoH,sc);}
float hash12(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}

void main(){
  float n=noise(v_noisePosition); n=abs(noise(n*3.0+100.0+v_noisePosition));
  float thr=0.04+pow(abs(v_uv.y*2.0-0.9),5.0)*5.0;
  float cr=aastep(thr,n);
  vec3 color=mix(u_color1,u_color0,cr);
  vec3 hsl=rgb2hsl(color); hsl.z=0.05+hsl.z*0.9; color=hsl2rgb(hsl);
  vec3 lp=vec3(1.0,1.5,1.0),ld=normalize(lp-v_worldPosition),wn=normalize(v_worldNormal),vd=normalize(cameraPosition-v_worldPosition);
  float r=cr*0.5+0.4; vec3 sc=color*0.3+vec3(0.6);
  float br=1.0/length(lp-v_worldPosition); br*=br;
  float diff=max(0.0,dot(wn,ld))/3.1415926*br;
  vec3 spec=getSpec(vd,wn,ld,r,sc)*br;
  color=color*(0.85+diff)+spec;
  float d=length((v_uv-0.5)*vec2(0.9,2.0));
  color=mix(color,u_colorBg,smoothstep(0.15,0.75,d));
  gl_FragColor=vec4(color+hash12(gl_FragCoord.xy)/255.0,1.0);
}
`;

// ── Post-processing: separable 9-tap Gaussian blur ───────────────────────────
const BLUR_VERT = /* glsl */`
varying vec2 v_uv;
void main() { v_uv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const BLUR_FRAG = /* glsl */`
uniform sampler2D tDiffuse;
uniform vec2 uDir;       // (radius/w, 0) or (0, radius/h)
varying vec2 v_uv;
void main() {
  // 9-tap Gaussian weights (sigma ≈ 1.5)
  vec4 c = vec4(0.0);
  c += texture2D(tDiffuse, v_uv - uDir * 4.0) * 0.0162;
  c += texture2D(tDiffuse, v_uv - uDir * 3.0) * 0.0540;
  c += texture2D(tDiffuse, v_uv - uDir * 2.0) * 0.1216;
  c += texture2D(tDiffuse, v_uv - uDir * 1.0) * 0.1945;
  c += texture2D(tDiffuse, v_uv             ) * 0.2270;
  c += texture2D(tDiffuse, v_uv + uDir * 1.0) * 0.1945;
  c += texture2D(tDiffuse, v_uv + uDir * 2.0) * 0.1216;
  c += texture2D(tDiffuse, v_uv + uDir * 3.0) * 0.0540;
  c += texture2D(tDiffuse, v_uv + uDir * 4.0) * 0.0162;
  gl_FragColor = c;
}
`;

// ── Seeded RNG ────────────────────────────────────────────────────────────────
let _seed = 0xdeadbeef;
function rng(): number {
  _seed = (Math.imul(1664525, _seed) + 1013904223) >>> 0;
  return _seed / 0xffffffff;
}

// ── Options ───────────────────────────────────────────────────────────────────
export type AmbientPalette = { bg: string; c0: string; c1: string };
export type AmbientOptions = {
  /** Render the instanced spheres (default true). false = flowing backdrop lines only. */
  spheres?: boolean;
  /** Fixed palette override; disables section palettes unless setTargetPalette is called. */
  palette?: AmbientPalette;
  /** Final canvas opacity after fade-in (default 0.55). */
  maxOpacity?: number;
  /** CSS z-index for the canvas (default '-1'). */
  zIndex?: string;
};

// ── SpheresBackground ─────────────────────────────────────────────────────────
export class SpheresBackground {
  private canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private clock = new THREE.Clock();
  private rafId: number | null = null;

  // Color objects (mutated each frame)
  private colorBg = new THREE.Color(DEFAULT_PALETTE.bg);
  private color0  = new THREE.Color(DEFAULT_PALETTE.c0);
  private color1  = new THREE.Color(DEFAULT_PALETTE.c1);
  private _tBg    = new THREE.Color(DEFAULT_PALETTE.bg);
  private _tC0    = new THREE.Color(DEFAULT_PALETTE.c0);
  private _tC1    = new THREE.Color(DEFAULT_PALETTE.c1);

  // Noise
  private noiseAccum = 0;
  private noiseOffset = 0;

  // Scroll parallax
  private scrollY      = 0;
  private easedScrollY = 0;

  // Activity - spikes on interaction, decays to idle
  private activity = 0;
  private readonly IDLE_ROT = 0.12;  // fraction of full rotation speed when idle (0–1)

  // Mouse
  private mouse      = new THREE.Vector2();
  private easedMouse = new THREE.Vector2();
  private lightVec   = new THREE.Vector3();

  // Post-processing (two-pass Gaussian blur)
  private rt1!: THREE.WebGLRenderTarget;
  private rt2!: THREE.WebGLRenderTarget;
  private postScene  = new THREE.Scene();
  private orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private blurMatH!: THREE.ShaderMaterial;
  private blurMatV!: THREE.ShaderMaterial;
  readonly blurRadius = 3;  // pixels - tweak to taste

  // Sphere data
  private readonly COUNT = 64;
  private sphereData: Array<{
    position: THREE.Vector3;
    rotation: THREE.Euler;
    quaternion: THREE.Quaternion;
    scale: THREE.Vector3;
  }> = [];
  private placements: THREE.Vector4[] = [];
  private noiseOffsets!: Float32Array;
  private instancedMesh!: THREE.InstancedMesh;
  private sphereUniforms!: Record<string, { value: unknown }>;
  private backdropUniforms!: Record<string, { value: unknown }>;
  private dummy = new THREE.Object3D();

  constructor(private opts: AmbientOptions = {}) {
    if (opts.palette) {
      this.colorBg.set(opts.palette.bg); this._tBg.set(opts.palette.bg);
      this.color0.set(opts.palette.c0);  this._tC0.set(opts.palette.c0);
      this.color1.set(opts.palette.c1);  this._tC1.set(opts.palette.c1);
    }
    this.canvas = document.createElement('canvas');
    Object.assign(this.canvas.style, {
      position: 'fixed', top: '0', left: '0',
      width: '100%', height: '100%',
      zIndex: opts.zIndex ?? '-1', pointerEvents: 'none',
      opacity: '0', transition: 'opacity 1000ms ease-in',
    });

    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setClearColor(this.colorBg, 1);

    this.scene  = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.01, 50);
    this.camera.position.z = 5;
    this.scene.add(this.camera);

    this._buildScene();

    window.addEventListener('resize', this._onResize);
    window.addEventListener('mousemove', this._onMouse);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onResize();
  }

  // ── Public API ──────────────────────────────────────────────────────────────
  getCanvas() { return this.canvas; }

  setTargetPalette(sectionId: string) {
    const p = SECTION_PALETTES[sectionId] ?? DEFAULT_PALETTE;
    this._tBg.set(p.bg);
    this._tC0.set(p.c0);
    this._tC1.set(p.c1);
  }

  start() {
    this._loop();
    // Deliberately dimmed: this layer is atmosphere, not foreground
    setTimeout(() => { this.canvas.style.opacity = String(this.opts.maxOpacity ?? 0.55); }, 50);
  }

  dispose() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('mousemove', this._onMouse);
    window.removeEventListener('scroll', this._onScroll);
    this.rt1?.dispose();
    this.rt2?.dispose();
    this.renderer.dispose();
    if (this.canvas.parentElement) this.canvas.parentElement.removeChild(this.canvas);
  }

  // ── Scene construction ──────────────────────────────────────────────────────
  private _buildScene() {
    const noiseOffVec = new THREE.Vector3(300 * rng(), 300 * rng(), 300 * rng());

    this.backdropUniforms = {
      u_noiseOffset:       { value: 0 },
      u_noiseOffsetVector: { value: noiseOffVec },
      u_color0:            { value: this.color0 },
      u_color1:            { value: this.color1 },
      u_colorBg:           { value: this.colorBg },
    };
    const backdropMat = new THREE.ShaderMaterial({
      vertexShader: BACKDROP_VERT, fragmentShader: BACKDROP_FRAG,
      uniforms: this.backdropUniforms as any,
      extensions: { derivatives: true } as any,
      depthWrite: false,
    });
    const backdrop = new THREE.Mesh(new THREE.PlaneGeometry(18, 10), backdropMat);
    backdrop.position.z = -2;
    this.scene.add(backdrop);

    // Spheres (optional - backdrop-only mode shows just the flowing lines)
    if (this.opts.spheres !== false) this._buildSpheres();

    // Post-processing setup
    const w = innerWidth, h = innerHeight;
    const rtOpts = { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter };
    this.rt1 = new THREE.WebGLRenderTarget(w, h, rtOpts);
    this.rt2 = new THREE.WebGLRenderTarget(w, h, rtOpts);

    this.blurMatH = new THREE.ShaderMaterial({
      vertexShader: BLUR_VERT, fragmentShader: BLUR_FRAG,
      uniforms: { tDiffuse: { value: this.rt1.texture }, uDir: { value: new THREE.Vector2(this.blurRadius / w, 0) } },
    });
    this.blurMatV = new THREE.ShaderMaterial({
      vertexShader: BLUR_VERT, fragmentShader: BLUR_FRAG,
      uniforms: { tDiffuse: { value: this.rt2.texture }, uDir: { value: new THREE.Vector2(0, this.blurRadius / h) } },
    });

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    // Two passes share the same geometry, swap material per-render in the loop
    quad.name = 'blurQuad';
    this.postScene.add(quad);
  }

  private _buildSpheres() {
    const geo = new THREE.IcosahedronGeometry(1, 3);
    this.noiseOffsets = new Float32Array(this.COUNT * 3);
    const ids = new Float32Array(this.COUNT);

    for (let i = 0; i < this.COUNT; i++) {
      const sc = 0.2 + 0.9 * Math.pow(rng(), 2);
      this.sphereData.push({
        position:   new THREE.Vector3(8 * (rng() - 0.5), 8 * (rng() - 0.5), -i / this.COUNT),
        rotation:   new THREE.Euler(rng() * Math.PI * 2, rng() * Math.PI * 2, rng() * Math.PI * 2),
        quaternion: new THREE.Quaternion(),
        scale:      new THREE.Vector3(sc, sc, sc),
      });
      this.placements.push(new THREE.Vector4(0, 0, 0, sc));
      this.noiseOffsets[i * 3]     = 300 * rng();
      this.noiseOffsets[i * 3 + 1] = 300 * rng();
      this.noiseOffsets[i * 3 + 2] = 300 * rng();
      ids[i] = i;
    }
    for (const d of this.sphereData) d.quaternion.setFromEuler(d.rotation);

    geo.setAttribute('a_instanceNoiseOffset', new THREE.InstancedBufferAttribute(this.noiseOffsets, 3));
    geo.setAttribute('a_id',                  new THREE.InstancedBufferAttribute(ids, 1));

    this.sphereUniforms = {
      u_color0:             { value: this.color0 },
      u_color1:             { value: this.color1 },
      u_colorBg:            { value: this.colorBg },
      u_lightPosition:      { value: new THREE.Vector3() },
      u_noiseOffset:        { value: 0 },
      u_opacity:            { value: 0 },
      u_instancePlacements: { value: this.placements },
    };
    const mat = new THREE.ShaderMaterial({
      vertexShader: SPHERE_VERT, fragmentShader: SPHERE_FRAG,
      uniforms: this.sphereUniforms as any,
      defines: { SPHERE_COUNT: this.COUNT },
      extensions: { derivatives: true } as any,
      transparent: true,
    });

    this.instancedMesh = new THREE.InstancedMesh(geo, mat, this.COUNT);
    this.instancedMesh.frustumCulled = false;
    this.scene.add(this.instancedMesh);
  }

  // ── Animation loop ──────────────────────────────────────────────────────────
  private _loop = () => {
    this.rafId = requestAnimationFrame(this._loop);
    const dt = Math.min(this.clock.getDelta(), 0.05);

    // Palette lerp (smooth 4% per frame)
    const t = 1 - Math.pow(0.96, dt * 60);
    this.colorBg.lerp(this._tBg, t);
    this.color0.lerp(this._tC0, t);
    this.color1.lerp(this._tC1, t);
    this.renderer.setClearColor(this.colorBg, 1);

    // Noise offset
    this.noiseAccum  += 0.12 * dt;
    this.noiseOffset += 0.2 * (this.noiseAccum - this.noiseOffset);
    (this.backdropUniforms['u_noiseOffset'] as any).value = this.noiseOffset;
    if (this.sphereUniforms) {
      (this.sphereUniforms['u_noiseOffset'] as any).value = this.noiseOffset;
      // Fade in
      const opU = this.sphereUniforms['u_opacity'] as any;
      opU.value = Math.min(0.75, opU.value + 0.01);
    }

    // Activity decay - halves roughly every 2 seconds
    this.activity *= Math.pow(0.985, dt * 60);

    // Rotation multiplier: idle floor + activity boost
    const rotMult = this.IDLE_ROT + (1 - this.IDLE_ROT) * this.activity;

    // Scroll ease
    this.easedScrollY += (this.scrollY - this.easedScrollY) * 0.06;
    // Convert px → world units: full page height ≈ 3× viewport, map to ±2 world units
    const scrollOffset = (this.easedScrollY / innerHeight) * 1.5;

    // Mouse easing
    this.easedMouse.x += (this.mouse.x - this.easedMouse.x) * 0.05;
    this.easedMouse.y += (this.mouse.y - this.easedMouse.y) * 0.05;
    this.camera.rotation.y =  this.easedMouse.x * 0.08;
    this.camera.rotation.x = -this.easedMouse.y * 0.06;

    // Light = unprojected mouse
    this.lightVec.set(this.easedMouse.x * 0.5, this.easedMouse.y * 0.5, 0.99525);
    this.lightVec.unproject(this.camera);
    if (this.sphereUniforms) {
      (this.sphereUniforms['u_lightPosition'] as any).value.copy(this.lightVec);
    }

    // Spheres - per-sphere parallax depth: spheres at the back scroll faster
    const pos = new THREE.Vector3();
    for (let i = 0; this.instancedMesh && i < this.COUNT; i++) {
      const d = this.sphereData[i]!;
      const variance = (34.762 * i + 6.64) % 1 - 0.5;
      const b = 4.0 * this.noiseOffset * 0.65 * (0.125 + 0.2 * variance);
      // Depth factor: spheres further back (more negative z) move faster on scroll
      const depthFactor = 1.0 + (i / this.COUNT) * 0.8;
      pos.copy(d.position);
      pos.x += 4.0 * Math.cos(b - 0.612 * Math.cos(b - 23.123 * i) + 421.632 * i);
      pos.y += 4.0 * Math.cos(b - 0.25  * Math.cos(b - 63.21  * i) + 32.76  * i);
      pos.y  = ((pos.y + 4.0) % 8.0 + 8.0) % 8.0 - 4.0;
      // Apply scroll parallax after wrap so it doesn't fight the looping
      pos.y -= scrollOffset * depthFactor;
      // Horizontal drift: alternates direction per sphere, scaled by depth
      const xSign = (i % 2 === 0 ? 1 : -1);
      const xRate = 0.3 + (i / this.COUNT) * 0.5;  // 0.3–0.8 units per viewport scroll
      pos.x += scrollOffset * xSign * xRate * depthFactor;
      d.rotation.x += 0.013 * ((21.512 * i + 9.23 ) % 1 - 0.5) * rotMult;
      d.rotation.y += 0.023 * ((632.65 * i + 4.23 ) % 1 - 0.5) * rotMult;
      d.rotation.z += 0.023 * ((34.762 * i + 6.64 ) % 1 - 0.5) * rotMult;
      d.quaternion.setFromEuler(d.rotation);
      this.dummy.position.copy(pos);
      this.dummy.quaternion.copy(d.quaternion);
      this.dummy.scale.copy(d.scale);
      this.dummy.updateMatrix();
      this.instancedMesh.setMatrixAt(i, this.dummy.matrix);
      this.placements[i]!.set(pos.x, pos.y, pos.z, d.scale.x);
    }
    if (this.instancedMesh) this.instancedMesh.instanceMatrix.needsUpdate = true;

    const quad = this.postScene.getObjectByName('blurQuad') as THREE.Mesh;
    // Pass 1: scene → rt1
    this.renderer.setRenderTarget(this.rt1);
    this.renderer.render(this.scene, this.camera);
    // Pass 2: horizontal blur rt1 → rt2
    quad.material = this.blurMatH;
    this.renderer.setRenderTarget(this.rt2);
    this.renderer.render(this.postScene, this.orthoCamera);
    // Pass 3: vertical blur rt2 → screen
    quad.material = this.blurMatV;
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.postScene, this.orthoCamera);
  };

  private _onResize = () => {
    const w = innerWidth, h = innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.rt1?.setSize(w, h);
    this.rt2?.setSize(w, h);
    if (this.blurMatH) (this.blurMatH.uniforms['uDir'] as any).value.set(this.blurRadius / w, 0);
    if (this.blurMatV) (this.blurMatV.uniforms['uDir'] as any).value.set(0, this.blurRadius / h);
  };

  private _onMouse = (e: MouseEvent) => {
    this.mouse.x =  (e.clientX / innerWidth  - 0.5) * 2;
    this.mouse.y = -(e.clientY / innerHeight - 0.5) * 2;
    this.activity = 1;
  };

  private _onScroll = () => {
    this.scrollY = window.scrollY;
    this.activity = 1;
  };
}

export function loadAnimation(
  container: HTMLElement | null = null,
  opts: AmbientOptions = {},
): SpheresBackground {
  const bg = new SpheresBackground(opts);
  (container ?? document.body).appendChild(bg.getCanvas());
  bg.start();
  return bg;
}
